import { Alert } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { uniqBy } from 'lodash';

import { api, assertNever, getQueryProps, isDefined } from 'common';

import { DataSourceDisplayNames } from '../../../../../../DocumentTemplates/Form/types.ts';
import { useFlowDefinitionContext } from '../../../../context.ts';
import { BlockAutocomplete } from '../../ConfigFields/Autocomplete';
import {
  BlockValueTemplateField,
  BlockValueTemplateFieldArea,
} from '../../ConfigFields/CodeMirror/FlowTemplate.tsx';
import { BlockHtmlField } from '../../ConfigFields/HtmlField.tsx';
import { BlockSelectField } from '../../ConfigFields/SelectField.tsx';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField.ts';

const GenerateFileMode = api.FlowDefinition.GenerateFile.Mode;
type GenerateFileMode = api.FlowDefinition.GenerateFile.Mode;

export function GenerateFileBlockFormFields() {
  const {
    flowDefinition: { organizationId },
  } = useFlowDefinitionContext();
  const { data: documentTemplates } = useQuery({
    ...getQueryProps(api.client.DocumentTemplate, 'findAll', { organizationId }),
  });
  const { value: mode } = useSelectedNodeField<GenerateFileMode>('config.mode');
  const { value: documentTemplateId } = useSelectedNodeField<string>('config.documentTemplateId');
  const { setValue: setValuesByDocumentTemplateKey } = useSelectedNodeField<Record<string, string>>(
    'config.valuesByDocumentTemplateKey'
  );

  const isDataSource = (dataSource: string): dataSource is api.DocumentTemplate.DataSource =>
    Object.values(api.DocumentTemplate.DataSource).includes(
      dataSource as api.DocumentTemplate.DataSource
    );
  const getDataSourceFromCommand = (
    command: api.DocumentTemplate.Command
  ): {
    label: string;
    apiKey: string | null;
  } => {
    const { dataSource, previsitFormFieldId } = command;
    if (isDataSource(dataSource)) {
      return {
        label: DataSourceDisplayNames[dataSource],
        apiKey: api.DocumentTemplate.DataSourceToDataSourceApiKeys[dataSource],
      };
    }
    if (isDefined(previsitFormFieldId)) {
      return {
        label: dataSource,
        apiKey: dataSource,
      };
    }
    return {
      label: dataSource,
      apiKey: null,
    };
  };
  const selectedDocumentTemplate = documentTemplates?.find(
    (template) => template.id === documentTemplateId
  );
  const selectedDocumentTemplateDataSources = isDefined(selectedDocumentTemplate)
    ? uniqBy(selectedDocumentTemplate.commands.map(getDataSourceFromCommand), 'label')
    : [];

  return (
    <>
      <BlockSelectField
        label="Generate File From"
        fieldName="config.mode"
        value={mode}
        options={[
          { label: 'Document Template', value: GenerateFileMode.DocumentTemplate },
          { label: 'Markdown', value: GenerateFileMode.Markdown },
          { label: 'HTML', value: GenerateFileMode.Html },
          { label: 'Base64 to File', value: GenerateFileMode.Base64ToFile },
        ]}
      />

      {(() => {
        if (!mode) {
          return null;
        }

        switch (mode) {
          case GenerateFileMode.DocumentTemplate:
            return (
              <>
                <BlockAutocomplete
                  fieldName="config.documentTemplateId"
                  label="Document Template"
                  options={documentTemplates?.map((template) => template.id) || []}
                  getOptionLabel={(documentTemplateId) =>
                    documentTemplates?.find((template) => template.id === documentTemplateId)
                      ?.name || 'Search'
                  }
                  helperText="Select a document template to use for this block"
                  onChange={() => {
                    // Reset the values on document template key when the document template is changed to avoid invalid combinations
                    setValuesByDocumentTemplateKey({});
                  }}
                />
                {selectedDocumentTemplateDataSources.map((dataSource) => {
                  const { label, apiKey } = dataSource;
                  if (!isDefined(apiKey)) {
                    return (
                      <Alert key={label} color="error">
                        Unsupported Data Source: {label}
                      </Alert>
                    );
                  }
                  return (
                    <BlockValueTemplateField
                      key={apiKey}
                      fieldName={`config.valuesByDocumentTemplateKey.${apiKey}`}
                      label={label}
                      required
                      helperText={`Value to use for the ${label} Data Source`}
                    />
                  );
                })}
              </>
            );

          case GenerateFileMode.Markdown:
            return (
              <BlockValueTemplateFieldArea
                fieldName="config.markdown"
                label="Markdown"
                helperText="The markdown to render in the document"
                placeholder="e.g. {{myMarkdown.data}}"
                required
              />
            );

          case GenerateFileMode.Html:
            return <BlockHtmlField fieldName="config.html" label="HTML" required />;

          case GenerateFileMode.Base64ToFile:
            return (
              <BlockValueTemplateFieldArea
                fieldName="config.base64Payload"
                label="Base64 Payload"
                helperText="The Base64-encoded payload to decode and store as a file"
                placeholder="e.g. {{myBlock.data.base64String}}"
                required
              />
            );

          default:
            assertNever('generateFileMode not implemented', mode);
        }
      })()}
    </>
  );
}
